import { Form, Formik, FormikValues } from 'formik';
import { useCreateOrganizationVideoMutation } from 'lib/api/organizationVideo/useCreateOrganizationVideoMutation';
import { OrganizationVideo } from 'lib/api/organizationVideo/useOrganizationVideoQuery';
import { useUpdateOrganizationVideoMutation } from 'lib/api/organizationVideo/useUpdateOrganizationVideoMutation';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { Flex, Gap } from 'lib/components/styles/layout';
import { useToastError } from 'lib/hooks';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-covideo-common';
import * as Yup from 'yup';
import { successToast } from 'lib/components/toasts/success';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { IOrganization, useAuth } from 'lib/context';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { FormikImageUpload } from 'lib/components/formik/FormikImageUpload/FormikImageUpload';
import { FormikVideoUpload } from 'lib/components/formik/FormikVideoUpload';
import { useUploadFile } from 'lib/hooks/useUploadFile';
import {
  OrganizationVideoFilter,
  useOrganizationVideoFiltersQuery,
} from 'lib/api/organizationVideo/useOrganizationVideoFiltersQuery';

const validationSchema = Yup.object({
  library: Yup.string().max(100, 'Library cannot be more than 100 characters.'),
  playlist: Yup.string().max(
    100,
    'Playlist cannot be more than 100 characters.'
  ),
  make: Yup.string().max(100, 'Make cannot be more than 100 characters.'),
  model: Yup.string().max(100, 'Model cannot be more than 100 characters.'),
  year: Yup.number(),
  title: Yup.string()
    .trim()
    .required('Title is required')
    .max(100, 'Title cannot be more than 100 characters.'),
  description: Yup.string(),
  video: Yup.object().test(
    'has-video',
    'Video is required',
    value => !!value?.url
  ),
  organizationId: Yup.number().required('Organization is required'),
});

type Props = {
  organizationVideo?: OrganizationVideo;
  onSuccess?: () => void;
  prefillData?: Omit<OrganizationVideo, 'organizationVideoId'>;
};

export const OrganizationVideoForm = (props: Props) => {
  const { organizationVideo, onSuccess, prefillData } = props;

  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);

  const {
    userData: { userOrganizationAccess },
  } = useAuth();

  const { showError } = useToastError();

  const { data: filterData, isLoading: isLoadingFilters } =
    useOrganizationVideoFiltersQuery();

  const [filterDictionary, setFilterDictionary] = useState<
    Record<string, OrganizationVideoFilter>
  >({});

  useEffect(() => {
    if (filterData) {
      const newFilterDictionary = (filterData.filters || []).reduce<
        Record<string, (typeof filterData.filters)[0]>
      >((acc, filter) => {
        acc[filter.key] = filter;
        return acc;
      }, {});
      setFilterDictionary(newFilterDictionary);
    }
  }, [filterData]);

  const { mutateAsync: handleUpdate, isLoading: isUpdating } =
    useUpdateOrganizationVideoMutation();

  const { mutateAsync: handleCreate, isLoading: isCreating } =
    useCreateOrganizationVideoMutation();

  const { uploadFile } = useUploadFile();

  const onSubmitHandler = async (values: FormikValues) => {
    setIsUploadingAssets(true);
    setVideoUploadProgress(0);
    setThumbnailUploadProgress(0);
    const [videoUrlRaw, thumbnailUrlRaw] = await Promise.all([
      values.video?.file
        ? uploadFile(values.video.file, setVideoUploadProgress)
        : Promise.resolve(values.video?.url ?? undefined),
      values.thumbnail?.file
        ? uploadFile(values.thumbnail.file, setThumbnailUploadProgress)
        : Promise.resolve(values.thumbnail?.url ?? undefined),
    ]);
    const videoUrl = values.video?.file
      ? videoUrlRaw
      : values.video?.url
        ? values.video.url
        : '';

    const thumbnailUrl = values.thumbnail?.file
      ? thumbnailUrlRaw
      : values.thumbnail?.url
        ? values.thumbnail.url
        : '';
    setIsUploadingAssets(false);
    try {
      const { video: _video, thumbnail: _thumbnail, ...rest } = values;
      const body = {
        ...rest,
        videoUrl,
        thumbnailUrl,
      };

      if (values.organizationVideoId) {
        await handleUpdate(body);
        successToast({
          title: 'Video successfully updated!',
        });
        onSuccess?.();
        return;
      }
      await handleCreate(body);
      successToast({
        title: 'Video successfully added!',
      });
      onSuccess?.();
    } catch (error) {
      showError(error);
    }
  };

  const userOrganizations = (userOrganizationAccess || [])
    .filter(organizationAccess => !!organizationAccess?.organization)
    .map(
      organizationAccess => organizationAccess.organization
    ) as IOrganization[];

  const userOrganizationOptions = (userOrganizations || []).map(
    organization => {
      return {
        label: organization.name,
        value: parseInt(organization.organizationId.toString(), 10),
      };
    }
  );

  const initialValues = {
    library: organizationVideo?.library ?? prefillData?.library ?? '',
    playlist: organizationVideo?.playlist ?? prefillData?.playlist ?? '',
    make: organizationVideo?.make ?? prefillData?.make ?? '',
    model: organizationVideo?.model ?? prefillData?.model ?? '',
    year:
      organizationVideo?.year ?? prefillData?.year ?? new Date().getFullYear(),
    title: organizationVideo?.title ?? prefillData?.title ?? '',
    description:
      organizationVideo?.description ?? prefillData?.description ?? '',
    video: {
      url: organizationVideo?.videoUrl ?? prefillData?.videoUrl ?? '',
      file: null,
    },
    thumbnail: {
      url: organizationVideo?.thumbnailUrl ?? prefillData?.thumbnailUrl ?? '',
      file: null,
    },
    organizationVideoId: organizationVideo?.organizationVideoId,
    organizationId:
      organizationVideo?.organizationId ??
      prefillData?.organizationId ??
      userOrganizationOptions[0].value,
  };

  const isLoading =
    isUpdating || isCreating || isUploadingAssets || isLoadingFilters;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmitHandler}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
    >
      {({ handleSubmit, isValid, setFieldValue }) => {
        const renderCreatableDropdown = (
          fieldName: 'model' | 'playlist' | 'library',
          fieldLabel: string
        ) => {
          const filter = filterDictionary[fieldName];
          // render dropdown if options exists, otherwise render input
          if (filter) {
            return (
              <FormikDropdownField
                name={fieldName}
                options={filter.options}
                label={fieldLabel}
                isRequired={false}
                disabled={isLoading}
                creatable
                isSearchable={true}
                onChange={option => {
                  if (option?.value) {
                    setFieldValue(fieldName, option.value);
                  }
                }}
                onCreateOption={inputValue => {
                  const newOption = {
                    value: inputValue,
                    label: inputValue,
                  };
                  setFilterDictionary(prev => ({
                    ...prev,
                    [fieldName]: {
                      ...prev[fieldName]!,
                      options: [...prev[fieldName]!.options, newOption],
                    },
                  }));
                  setFieldValue(fieldName, inputValue);
                }}
              />
            );
          }
          return (
            <FormikInputField
              label={fieldLabel}
              name={fieldName}
              type='text'
              isRequired={false}
              disabled={isLoading}
              onChange={e => setFieldValue(fieldName, e.target.value)}
            />
          );
        };
        return (
          <Form onSubmit={handleSubmit}>
            <Flex height='100%'>
              <Gap flexWrap='nowrap' alignItems='flex-start' height='100%'>
                <Flex height='100%'>
                  {userOrganizationOptions.length > 1 && (
                    <FormikDropdownField
                      name='organizationId'
                      options={userOrganizationOptions}
                      label='Organization'
                      isRequired={true}
                      disabled={isLoading}
                    />
                  )}
                  <FormikInputField
                    label='Title'
                    name='title'
                    type='text'
                    isRequired
                    autoFocus={true}
                    disabled={isLoading}
                    onChange={e => setFieldValue('title', e.target.value)}
                  />
                  <Gap flexWrap='nowrap'>
                    {renderCreatableDropdown('model', 'Model')}
                    <FormikInputField
                      label='Year'
                      name='year'
                      type='number'
                      isRequired={false}
                      disabled={isLoading}
                      onChange={e =>
                        setFieldValue(
                          'year',
                          e.target.value ? parseInt(e.target.value, 10) : 0
                        )
                      }
                    />
                  </Gap>
                  <Gap flexWrap='nowrap'>
                    {renderCreatableDropdown('playlist', 'Playlist')}
                    {renderCreatableDropdown('library', 'Library')}
                  </Gap>
                  <FormikTextAreaField
                    name='description'
                    label='Description'
                    isRequired={false}
                    disabled={isLoading}
                    onChange={e => setFieldValue('description', e.target.value)}
                  />
                </Flex>
                <Flex>
                  <FormikVideoUpload
                    name='video'
                    label='Video'
                    isRequired
                    disabled={isLoading}
                    progress={videoUploadProgress}
                    extendStyles={{
                      videoStyles: {
                        height: 200,
                      },
                    }}
                  />
                  <FormikImageUpload
                    name='thumbnail'
                    label='Thumbnail'
                    isRequired={false}
                    disabled={isLoading}
                    targetWidth={500}
                    progress={thumbnailUploadProgress}
                    extendStyles={{
                      containerStyles: { height: '140px' },
                    }}
                  />
                </Flex>
              </Gap>
              <Gap
                width='100%'
                justifyContent='flex-end'
                gap='8px'
                height='100%'
              >
                {onSuccess && (
                  <Button
                    text={'Cancel'}
                    disabled={isLoading}
                    variant='secondary'
                    onClick={() => {
                      if (onSuccess) {
                        onSuccess();
                      }
                    }}
                  />
                )}
                <Button
                  text={isLoading ? 'Saving...' : 'Save'}
                  disabled={!isValid || isLoading}
                  type='submit'
                />
              </Gap>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};
